import { useState } from 'react';
import {
  TablePagination,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableHead,
} from '@mui/material';
import {
  DialogContainer,
  DialogBody,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../../common/components';
import { useExcelExport } from '../../hooks/useExcelExport';
import { usePDFExport } from '../../hooks/usePDFExport';
import { PAGINATION_CONFIG } from '../../constants/statisticsConstants';
import { formatCurrency } from '../../utils/statisticsUtils';
import StatisticsDialogTitle from '../common/StatisticsDialogTitle';

const SubscriberReportModal = ({ open, handleClose, header, results }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_CONFIG.DEFAULT_ROWS_PER_PAGE);

  const { exportToExcel } = useExcelExport();
  const { exportBlockFormatToPDF } = usePDFExport();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToExcel = () => {
    const data = results.map((result) => ({
      Grupo: result.grupo,
      'N. Contrato': result.n_contrato,
      'N. Solicitud': result.n_solicitud,
      Apellido: result.apellido,
      Nombre: result.nombre,
      DNI: result.dni,
      Email: result.mail,
      'Fecha Nacimiento': result.fecha_nac,
      Dirección: result.direccion,
      Distrito: result.distrito,
      Localidad: result.localidad,
      Provincia: result.provincia,
      'Código Postal': result.cp,
      Teléfono: result.tel,
      'Entidad Intermediaria': result.entidad_int,
      'Seguro de Vida': result.seguro_vida,
      'Fecha Suscripción': result.fecha_suscrip,
      'Fecha Adjudicación': result.fecha_adjudic,
      'Fecha Poseedor': result.fecha_poseedor,
      'Lote Propio': result.lote_propio ? 'Sí' : 'No',
      'Tipo Construcción': result.tipo_construc,
    }));

    exportToExcel(data, 'ReporteSuscriptores');
  };

  const handleExportToPDF = () => {
    const headers = [
      'Grupo',
      'N. Contrato',
      'N. Solicitud',
      'Apellido',
      'Nombre',
      'DNI',
      'Email',
      'Fecha Nac.',
      'Dirección',
      'Distrito',
      'Localidad',
      'Provincia',
      'CP',
      'Teléfono',
      'Entidad Int.',
      'Seguro Vida',
      'Fecha Suscrip.',
      'Fecha Adjudic.',
      'Fecha Poseedor',
      'Lote Propio',
      'Tipo Construc.',
    ];

    const data = results.map((result) => [
      result.grupo,
      result.n_contrato,
      result.n_solicitud,
      result.apellido,
      result.nombre,
      result.dni,
      result.mail,
      result.fecha_nac,
      result.direccion,
      result.distrito,
      result.localidad,
      result.provincia,
      result.cp,
      result.tel,
      result.entidad_int,
      result.seguro_vida ? formatCurrency(result.seguro_vida) : 'N/A',
      result.fecha_suscrip,
      result.fecha_adjudic,
      result.fecha_poseedor,
      result.lote_propio ? 'Sí' : 'No',
      result.tipo_construc,
    ]);

    exportBlockFormatToPDF({
      title: 'Reporte de Suscriptores',
      headers,
      data,
      filename: 'ReporteSuscriptores',
      columnsPerRow: 5,
    });
  };

  if (!header || !results || results.length === 0) {
    return null;
  }

  const paginatedResults = results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DialogContainer open={open} onClose={handleClose} size="fullWidth">
      <StatisticsDialogTitle
        title="Reporte de Suscriptores"
        onExportExcel={handleExportToExcel}
        onExportPDF={handleExportToPDF}
        onClose={handleClose}
      />
      <DialogBody>
        <Box
          sx={{ mb: 3, textAlign: 'center', border: '1px solid #4a90e2', p: 2, borderRadius: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
            {header.constructora_info}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
            Actualizado a la fecha: {header.updated_at_date}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            Grupo: {header.group_name || 'Todos'}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', color: '#555' }}>
          Cantidad de suscriptores encontrados: {results.length}
        </Typography>

        <GeneralTableBox>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <HeadTableCell>Grupo</HeadTableCell>
                <HeadTableCell>N. Contrato</HeadTableCell>
                <HeadTableCell>N. Solicitud</HeadTableCell>
                <HeadTableCell>Apellido</HeadTableCell>
                <HeadTableCell>Nombre</HeadTableCell>
                <HeadTableCell>DNI</HeadTableCell>
                <HeadTableCell>Email</HeadTableCell>
                <HeadTableCell>Fecha Nac.</HeadTableCell>
                <HeadTableCell>Dirección</HeadTableCell>
                <HeadTableCell>Distrito</HeadTableCell>
                <HeadTableCell>Localidad</HeadTableCell>
                <HeadTableCell>Provincia</HeadTableCell>
                <HeadTableCell>CP</HeadTableCell>
                <HeadTableCell>Teléfono</HeadTableCell>
                <HeadTableCell>Entidad Int.</HeadTableCell>
                <HeadTableCell>Seguro Vida</HeadTableCell>
                <HeadTableCell>Fecha Suscrip.</HeadTableCell>
                <HeadTableCell>Fecha Adjudic.</HeadTableCell>
                <HeadTableCell>Fecha Poseedor</HeadTableCell>
                <HeadTableCell>Lote Propio</HeadTableCell>
                <HeadTableCell>Tipo Construc.</HeadTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResults.map((result, index) => (
                <TableRow key={index}>
                  <RowTableCell>{result.grupo}</RowTableCell>
                  <RowTableCell>{result.n_contrato}</RowTableCell>
                  <RowTableCell>{result.n_solicitud}</RowTableCell>
                  <RowTableCell>{result.apellido}</RowTableCell>
                  <RowTableCell>{result.nombre}</RowTableCell>
                  <RowTableCell>{result.dni}</RowTableCell>
                  <RowTableCell>{result.mail}</RowTableCell>
                  <RowTableCell>{result.fecha_nac}</RowTableCell>
                  <RowTableCell>{result.direccion}</RowTableCell>
                  <RowTableCell>{result.distrito}</RowTableCell>
                  <RowTableCell>{result.localidad}</RowTableCell>
                  <RowTableCell>{result.provincia}</RowTableCell>
                  <RowTableCell>{result.cp}</RowTableCell>
                  <RowTableCell>{result.tel}</RowTableCell>
                  <RowTableCell>{result.entidad_int}</RowTableCell>
                  <RowTableCell>
                    {result.seguro_vida ? formatCurrency(result.seguro_vida) : 'N/A'}
                  </RowTableCell>
                  <RowTableCell>{result.fecha_suscrip}</RowTableCell>
                  <RowTableCell>{result.fecha_adjudic}</RowTableCell>
                  <RowTableCell>{result.fecha_poseedor}</RowTableCell>
                  <RowTableCell>{result.lote_propio ? 'Sí' : 'No'}</RowTableCell>
                  <RowTableCell>{result.tipo_construc}</RowTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </GeneralTableBox>

        <TablePagination
          component="div"
          count={results.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Suscriptores por página"
          rowsPerPageOptions={PAGINATION_CONFIG.ROWS_PER_PAGE_OPTIONS}
        />
      </DialogBody>
    </DialogContainer>
  );
};

export default SubscriberReportModal;
